// Ship by job (the Shipping hub's tab; the old "Ready to Ship" queue): ONE copy of its reads, its selection and
// quantity rules, the yield / date / load checks and the POST /shipping/finalize_batch, for the desktop
// (pages/ShippingQueuePage.jsx) and the phone (mobile/screens/shipping/ShipByJobTab.jsx), moved out of the page
// 2026-10-29 with the desktop's words and requests unchanged.
//
// Lines come from GET /queue/shipping_details. Assembly lines (ppq > 1) show and edit their quantities in
// FINISHED-ASSEMBLY units; storage (jt.quantity / qty_completed / SO lines) is primary-part-space
// (assemblies x ppq), converted once in `shipBatch`. A line of an order with a live packing plan is closed: it ships
// from its package guide (the hub's board, soBoard.livePlanOrderIds).
import { detailToString } from '../../utils/apiError';
import { absoluteUrl } from '../signing/signRequestsApi';
import { jobLoadBody } from './JobLoadBox';

/** 1 for non-assembly lines and ppq=1 assemblies (no conversion), else the line's primary_part_quantity. */
export function lineAssemblyPpq(line) {
  const ppq = parseInt(line?.primary_part_quantity, 10) || 1;
  return (line?.assembly_id && ppq > 1) ? ppq : 1;
}

/** The selection key of one row: a part (`job--part--task`) or an assembly's consumable (`job--cons-N--task`). */
export function makeLineKey(jobId, partId, taskId, consumableId = null) {
  if (consumableId) {
    return `${jobId}--cons-${consumableId}--${taskId}`;
  }
  return `${jobId}--${partId}--${taskId}`;
}

/** A Date (local) or 'YYYY-MM-DD' -> 'YYYY-MM-DD'; '' when there is none. */
export function formatShipDate(date) {
  if (!date) return '';
  if (typeof date === 'string') return /^\d{4}-\d\d-\d\d$/.test(date) ? date : '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** The Shipping Manifest for a day (all customers, or one). */
export function shippingManifestPath(date, customerId = '') {
  let path = `/reports/shipping/${formatShipDate(date)}`;
  if (customerId) path += `?customer_id=${customerId}`;
  return path;
}

// --- reads ---------------------------------------------------------------------------------------

/** GET /queue/shipping_details -> {ok, lines, message}. */
export async function loadShippingLines(apiBase) {
  try {
    const res = await fetch(`${apiBase}/queue/shipping_details`);
    if (!res.ok) throw new Error('Failed to load shipping details');
    const data = await res.json();
    return { ok: true, lines: Array.isArray(data) ? data : [], message: '' };
  } catch (err) {
    console.error('Error loading shipping lines:', err);
    return { ok: false, lines: [], message: (err && err.message) || 'Failed to load shipping details' };
  }
}

/** GET /customers, sorted by name -> {ok, customers}. */
export async function loadCustomers(apiBase) {
  try {
    const res = await fetch(`${apiBase}/customers`);
    if (!res.ok) throw new Error('Failed to load customers');
    const data = await res.json();
    return { ok: true, customers: (data || []).sort((a, b) => a.name.localeCompare(b.name)) };
  } catch (err) {
    console.error('Error loading customers:', err);
    return { ok: false, customers: [] };
  }
}

/** The selection for freshly read lines: nothing ticked; each row's ship qty = the API's suggestion (else ordered -
 *  defects), an assembly parent in whole finished assemblies (floor: a partial assembly's worth of loose primary
 *  parts isn't a shippable unit); each non-primary component = its ordered - defects. */
export function initialSelection(lines) {
  const state = {};
  (lines || []).forEach((line) => {
    const mainKey = makeLineKey(line.job_id, line.part_id, line.task_id);
    const rawFinalShipped = line.suggested_ship_qty != null
      ? line.suggested_ship_qty
      : Math.max(0, (line.quantity_ordered || 0) - (line.defects || 0));
    state[mainKey] = { selected: false, finalShipped: Math.floor(rawFinalShipped / lineAssemblyPpq(line)) };
    if (line.assembly_id && line.assembly_components) {
      line.assembly_components.forEach((comp) => {
        if (comp.is_primary) return;
        const compKey = makeLineKey(line.job_id, comp.part_id, line.task_id, comp.consumable_id);
        state[compKey] = { selected: false, finalShipped: Math.max(0, (comp.quantity_ordered || 0) - (comp.defects || 0)) };
      });
    }
  });
  return state;
}

/** The customers that have lines in the queue: [{customer_id, name}]. */
export function queueCustomers(lines) {
  const uniqueMap = new Map();
  (lines || []).forEach((line) => {
    if (line.customer_id && line.customer_name) uniqueMap.set(line.customer_id, line.customer_name);
  });
  return Array.from(uniqueMap, ([id, name]) => ({ customer_id: id, name }));
}

/** Rows sorted by one field (a copy; nulls read as ''). No field = as read. */
export function sortRows(rows, field, direction = 'asc') {
  const result = [...(rows || [])];
  if (!field) return result;
  result.sort((a, b) => {
    const valA = a[field] ?? '';
    const valB = b[field] ?? '';
    if (valA < valB) return direction === 'asc' ? -1 : 1;
    if (valA > valB) return direction === 'asc' ? 1 : -1;
    return 0;
  });
  return result;
}

/** The queue's filters: product id contains, sales order number contains, one customer; then the sort. */
export function filterShippingLines(lines, { product = '', salesOrder = '', customerId = '', sortField = null, sortDirection = 'asc' } = {}) {
  let result = [...(lines || [])];
  if (String(product).trim()) {
    const v = String(product).trim().toLowerCase();
    result = result.filter((line) => String(line.product_id).toLowerCase().includes(v));
  }
  if (String(salesOrder).trim()) {
    const v = String(salesOrder).trim().toLowerCase();
    result = result.filter((line) => line.sales_order_number && String(line.sales_order_number).toLowerCase().includes(v));
  }
  if (customerId) {
    const id = Number(customerId);
    result = result.filter((line) => line.customer_id === id);
  }
  return sortRows(result, sortField, sortDirection);
}

/** Whether a line ships from its package guide (its order has a live packing plan). `liveSoIds`: a Set or null. */
export function lineClosedFor(liveSoIds, line) {
  return !!(liveSoIds && line && line.sales_order_id != null && liveSoIds.has(Number(line.sales_order_id)));
}

/** Lines grouped by their sales order for a narrow screen, in list order: [{key, orderNumber, customerName, lines}];
 *  lines on no order group by customer ("No sales order"). */
export function groupByOrder(lines) {
  const groups = new Map();
  (lines || []).forEach((line) => {
    const key = line.sales_order_id != null ? `so-${line.sales_order_id}`
      : line.sales_order_number ? `son-${line.sales_order_number}` : `cust-${line.customer_id ?? ''}`;
    if (!groups.has(key)) {
      groups.set(key, { key, orderNumber: line.sales_order_number || null, customerName: line.customer_name || '', lines: [] });
    }
    groups.get(key).lines.push(line);
  });
  return Array.from(groups.values());
}

// --- selection -------------------------------------------------------------------------------------

/** Tick / untick one row (a component row never ticks on its own: its assembly's header does). */
export function setRowSelected(selected, key, checked) {
  if (!selected[key]) return selected;
  return { ...selected, [key]: { ...selected[key], selected: checked } };
}

/** Tick / untick an assembly header: the assembly and every component with it. */
export function setAssemblySelected(selected, line, checked) {
  const next = { ...selected };
  const mainKey = makeLineKey(line.job_id, line.part_id, line.task_id);
  next[mainKey] = { ...next[mainKey], selected: checked };
  (line.assembly_components || []).forEach((comp) => {
    const compKey = makeLineKey(line.job_id, comp.part_id, line.task_id, comp.consumable_id);
    if (next[compKey]) next[compKey] = { ...next[compKey], selected: checked };
  });
  return next;
}

/** Select all: every visible line that is not closed, with its components. */
export function selectAllVisible(selected, visibleLines, checked, isClosed = () => false) {
  const next = { ...selected };
  (visibleLines || []).forEach((line) => {
    if (isClosed(line)) return;
    const lineKey = makeLineKey(line.job_id, line.part_id, line.task_id);
    if (next[lineKey]) next[lineKey] = { ...next[lineKey], selected: checked };
    (line.assembly_components || []).forEach((comp) => {
      const compKey = makeLineKey(line.job_id, comp.part_id, line.task_id, comp.consumable_id);
      if (next[compKey]) next[compKey] = { ...next[compKey], selected: checked };
    });
  });
  return next;
}

/** A typed ship qty (blank / negative / not a number = 0). On an assembly's header it also sets every non-primary
 *  component to qty x quantity_per_assembly. */
export function setShipQuantity(selected, lines, { jobId, partId, taskId, value, consumableId = null }) {
  const key = makeLineKey(jobId, partId, taskId, consumableId);
  let parsed = parseInt(value, 10);
  if (isNaN(parsed) || parsed < 0) parsed = 0;
  const next = { ...selected };
  if (next[key]) next[key] = { ...next[key], finalShipped: parsed };
  const parentLine = (lines || []).find((line) => line.task_id === taskId);
  if (parentLine && parentLine.assembly_id && parentLine.assembly_components
    && parentLine.part_id === partId && !consumableId) {
    parentLine.assembly_components.forEach((comp) => {
      if (comp.is_primary) return;
      const compKey = makeLineKey(parentLine.job_id, comp.part_id, parentLine.task_id, comp.consumable_id);
      if (next[compKey]) next[compKey] = { ...next[compKey], finalShipped: parsed * comp.quantity_per_assembly };
    });
  }
  return next;
}

// --- the batch and its rules -------------------------------------------------------------------------

/** The ticked lines as finalize_batch items (one per task, in primary-part-space) and the per-SO-line totals the
 *  yield rules check: {items, aggregates: [{soNumber, product, policy, type, allowOverShip, totalOrdered,
 *  totalHistoric, currentBatchSum, jobNumbers}]}. Closed lines never ship here. */
export function shipBatch(selected, lines, isClosed = () => false) {
  const items = [];
  const shippedTaskIds = new Set();
  const soAggregates = new Map();
  Object.keys(selected || {}).forEach((key) => {
    if (!selected[key].selected) return;
    // the task id is the 3rd part of the key in both formats (part and --cons-N--)
    const taskId = Number(key.split('--')[2]);
    const parentLine = (lines || []).find((line) => line.task_id === taskId);
    if (!parentLine || shippedTaskIds.has(parentLine.task_id) || isClosed(parentLine)) return;
    const parentKey = makeLineKey(parentLine.job_id, parentLine.part_id, parentLine.task_id);
    const inputQty = selected[parentKey]?.finalShipped || 0;
    const currentShipQty = inputQty * lineAssemblyPpq(parentLine);
    items.push({
      job_id: parentLine.job_id,
      part_id: parentLine.part_id,
      task_id: parentLine.task_id,
      final_shipped_qty: currentShipQty,
    });
    shippedTaskIds.add(parentLine.task_id);
    // grouped by "SalesOrderID-PartID": several jobs for the same SO line add up
    if (parentLine.sales_order_id && parentLine.so_line_total_ordered) {
      const groupKey = `${parentLine.sales_order_id}-${parentLine.part_id}`;
      if (!soAggregates.has(groupKey)) {
        soAggregates.set(groupKey, {
          soNumber: parentLine.sales_order_number,
          product: parentLine.product_id,
          policy: parentLine.yield_policy_name || 'Standard',
          type: parentLine.min_required_type, // 'Ordered' or 'None'
          allowOverShip: parentLine.allow_over_ship,
          totalOrdered: parentLine.so_line_total_ordered,
          totalHistoric: parentLine.so_line_total_shipped_historic,
          currentBatchSum: 0,
          jobNumbers: [],
        });
      }
      const group = soAggregates.get(groupKey);
      group.currentBatchSum += currentShipQty;
      group.jobNumbers.push(parentLine.master_job_num);
    }
  });
  return { items, aggregates: Array.from(soAggregates.values()) };
}

/** The yield policy over the batch: short of 'Ordered' = a warning the operator may override; over the order when
 *  the policy forbids over-shipping = a hard block. -> {hardBlocks: [text], softWarnings: [text]}. */
export function yieldChecks(aggregates) {
  const hardBlocks = [];
  const softWarnings = [];
  (aggregates || []).forEach((data) => {
    const totalProjected = data.totalHistoric + data.currentBatchSum;
    const qtyOrdered = data.totalOrdered;
    if (data.type === 'Ordered' && totalProjected < qtyOrdered) {
      const shortBy = qtyOrdered - totalProjected;
      softWarnings.push(
        `• SO #${data.soNumber} (${data.product}):\n` +
        `   Policy: ${data.policy}\n` +
        `   Ordered: ${qtyOrdered}, Projected Total: ${totalProjected}\n` +
        `   ⚠️ SHORTFALL: ${shortBy} (Partial Shipment)`
      );
    }
    if (totalProjected > qtyOrdered && data.allowOverShip === false) {
      const overBy = totalProjected - qtyOrdered;
      hardBlocks.push(
        `• SO #${data.soNumber} (${data.product}):\n` +
        `   Ordered: ${qtyOrdered}, Projected Total: ${totalProjected}\n` +
        `   ⛔ OVER by ${overBy} — policy forbids shipping more than ordered`
      );
    }
  });
  return { hardBlocks, softWarnings };
}

export const NO_LINES_TEXT = 'No lines selected for shipping.';
export const NO_DATE_TEXT = 'Please select a shipment date.';
export const NO_PALLET_TEXT = 'Name the pallet first: scan its beacon or label, type its number, or tap New pallet without a beacon.';

export function overShipText(hardBlocks) {
  return '⛔ SHIPMENT BLOCKED — OVER-SHIPMENT NOT ALLOWED ⛔\n\n'
    + hardBlocks.join('\n\n')
    + '\n\nReduce quantities to proceed.';
}

export function yieldWarningText(softWarnings) {
  return '⚠️ SALES ORDER YIELD WARNINGS ⚠️\n\n'
    + 'The current selection conflicts with the yield policy:\n\n'
    + softWarnings.join('\n\n')
    + '\n\nDo you want to proceed with this shipment anyway?';
}

/** The finalize_batch body; with a load box (shipping_signing.job_loads) the load rides along. */
export function finalizeBatchBody({ items, pin, shipDate, notifyCustomer, jobLoad = null }) {
  const body = {
    items,
    pin,
    ship_date: formatShipDate(shipDate),
    notify_customer: notifyCustomer,
  };
  if (jobLoad) body.load = jobLoadBody(jobLoad, items.map((it) => it.task_id));
  return body;
}

/**
 * Ship Selected. Every rule in order: nothing ticked; an over-shipment (hard block); a shortfall (the operator may
 * go on); a ship date; with a load box, its pallet; then who ships (`askPin`: the Working-as session on the hub, the
 * PIN prompt without it) and POST /shipping/finalize_batch. Tells the person through `ui` (alert / confirm). A load's
 * manifest opens through ui.openPdfTab when the ui has one (desktop); otherwise it is handed back for the screen to
 * offer (phone). -> {done, batchId?, manifestUrl?, loaded?}; done=false when a rule stopped it, the person backed out
 * or the API refused (already said).
 */
export async function shipSelected({
  apiBase, ui, selected, lines, isClosed = () => false, shipDate, notifyCustomer = false, jobLoad = null, askPin,
}) {
  const { items, aggregates } = shipBatch(selected, lines, isClosed);
  if (items.length === 0) {
    await ui.alert(NO_LINES_TEXT);
    return { done: false };
  }
  const { hardBlocks, softWarnings } = yieldChecks(aggregates);
  if (hardBlocks.length > 0) {
    await ui.alert(overShipText(hardBlocks));
    return { done: false };
  }
  if (softWarnings.length > 0) {
    if (!(await ui.confirm(yieldWarningText(softWarnings)))) return { done: false };
  }
  if (!shipDate || !formatShipDate(shipDate)) {
    await ui.alert(NO_DATE_TEXT);
    return { done: false };
  }
  if (jobLoad && !jobLoad.pallet) {
    await ui.alert(NO_PALLET_TEXT);
    return { done: false };
  }
  const pin = await askPin();
  if (!pin) return { done: false };

  const body = finalizeBatchBody({ items, pin, shipDate, notifyCustomer, jobLoad });
  try {
    const res = await fetch(`${apiBase}/shipping/finalize_batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(detailToString(err.detail) || 'Error finalizing shipping');
    }
    const data = await res.json();
    const batchId = data.shipment_group_id || 'Unknown';
    if (!jobLoad) {
      await ui.alert(`Shipping completed successfully.\n\nBatch ID: ${batchId}`);
      return { done: true, batchId, loaded: false, manifestUrl: null };
    }
    const manifest = data.load && data.load.manifest_url;
    const manifestUrl = manifest ? absoluteUrl(apiBase, manifest) : null;
    const opens = !!ui.openPdfTab;
    await ui.alert(`Loaded on ${jobLoad.pallet.label}.\n\nBatch ID: ${batchId}`
      + (manifest ? (opens ? "\n\nThe load's manifest opens to print." : "\n\nThe load's manifest is ready to print or share.") : ''));
    if (manifestUrl && opens) {
      try { await ui.openPdfTab(manifestUrl, `Manifest_${batchId}.pdf`); } catch (_) { /* reprint from History */ }
    }
    return { done: true, batchId, loaded: true, manifestUrl };
  } catch (err) {
    await ui.alert('Error shipping items: ' + err.message);
    return { done: false };
  }
}
